import * as React from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  styled,
} from "@mui/material";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { MemoryFact, MemoryFactSource } from "common/services/stream";
import { getConversationShortCode } from "../requests";
import { ROUTES } from "common/constant/routes";

// A compact pill shown under an assistant answer that drew on cross-conversation
// patient memory. Clicking it opens a dialog that, for each recalled fact, quotes
// the original clinician message it was extracted from ("where this came from").
//
// Wording is deliberately "recalled from memory", not "cited": we surface every
// fact retrieved for the turn, which is a superset of what the model actually
// used (the model's real usage lives in the inference server, out of scope here).

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

const SourceButton = styled("button")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  background: "transparent",
  borderRadius: 5,
  padding: "0 8px",
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  fontSize: 12,
  height: 25,
  minHeight: 25,
  color: theme.palette.text.secondary,
  "&:hover": {
    background: theme.palette.action.hover,
  },
}));

const SourceQuote = styled("blockquote")(({ theme }) => ({
  margin: theme.spacing(1, 0, 0, 0),
  padding: theme.spacing(1, 1.5),
  borderLeft: `3px solid ${theme.palette.primary.main}`,
  background: theme.palette.action.hover,
  borderRadius: 4,
  color: theme.palette.text.primary,
  fontSize: 13,
  whiteSpace: "pre-wrap",
}));

interface MemorySourcesProps {
  memoryFacts?: MemoryFact[] | null;
}

export default function MemorySources({ memoryFacts }: MemorySourcesProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const facts = React.useMemo(
    () => (Array.isArray(memoryFacts) ? memoryFacts.filter((f) => f && f.fact) : []),
    [memoryFacts]
  );

  // Jump to the chat turn a fact was extracted from: resolve the conversation's
  // shortCode (route is /c/:shortcode), then navigate with a #msg-<id> hash that
  // Chat.tsx scrolls to once that conversation's history renders. Same-conversation
  // clicks still work — navigating to the same route just updates the hash.
  const jumpToSource = React.useCallback(
    async (source: MemoryFactSource) => {
      if (!source.conversation_id || !source.message_id) return;
      const shortCode = await getConversationShortCode(source.conversation_id);
      if (!shortCode) return;
      setOpen(false);
      navigate(`${ROUTES.CONVERSATION}/${shortCode}#msg-${source.message_id}`);
    },
    [navigate]
  );

  if (facts.length === 0) return null;

  const label = t("chat.memory.pill", { count: facts.length });

  return (
    <>
      <SourceButton onClick={() => setOpen(true)} type="button">
        <PsychologyOutlinedIcon sx={{ fontSize: 16, color: "primary.main" }} />
        <span>{label}</span>
      </SourceButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PsychologyOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
          {t("chat.memory.dialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("chat.memory.dialogSubtitle")}
          </Typography>
          {facts.map((fact, i) => {
            const validDate = formatDate(fact.valid_at);
            return (
              <Box key={i} sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fact.fact}
                </Typography>
                {validDate && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("chat.memory.validSince", { date: validDate })}
                    sx={{ mt: 0.5, mr: 0.5, height: 20, fontSize: 11 }}
                  />
                )}
                {(fact.sources || []).map((source, j) => {
                  const sourceDate = formatDate(source.timestamp);
                  const canJump = Boolean(source.conversation_id && source.message_id);
                  return (
                    <Box key={j}>
                      <SourceQuote>{source.content}</SourceQuote>
                      {canJump ? (
                        <Typography
                          variant="caption"
                          onClick={() => jumpToSource(source)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                            color: "primary.main",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {t("chat.memory.sourceFrom", {
                            date: sourceDate || t("chat.memory.unknownDate"),
                          })}
                          <NorthEastIcon sx={{ fontSize: 13 }} />
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {t("chat.memory.sourceFrom", {
                            date: sourceDate || t("chat.memory.unknownDate"),
                          })}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                {(!fact.sources || fact.sources.length === 0) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {t("chat.memory.noSource")}
                  </Typography>
                )}
                {i < facts.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
}
